import { isDefined } from '@sgme/fp';
import { useContext } from 'react';

import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import { OrderRow } from '@/App/tiles/orders/components/OrderRow.tsx';
import {
  StopLossContext,
  useValidateStopLoss,
} from '@/App/tiles/orders/StopLossOrder/StopLossOrderCreationTile/stopLossContext.tsx';
import { SgNumberInput } from '@/components/sg-design-system/components/input/SgNumberInput';
import { OmsFieldErrorWrapper } from '@/components/ui/OmsFieldErrorWrapper.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import {
  selectOrderEntryByIdFromBlotter,
  selectStopLossErrors,
  selectTradeIdFromStopLossTile,
} from '@/store/orders/stopLoss/selectors.ts';

type StopLossLimitPriceProps = {
  limitPrice: number | undefined;
  setLimitPrice: (value: number | undefined) => void;
};

export const StopLossEditingLimitPrice = (props: StopLossLimitPriceProps) => {
  const track = useTrackEvent();
  const { limitPrice, setLimitPrice } = props;
  const { setIsModifyingField, tileId } = useContext(StopLossContext);

  const tradeId = useAppSelector((state) => selectTradeIdFromStopLossTile(state, tileId));
  const orderFromBlotter = useAppSelector((state) =>
    tradeId ? selectOrderEntryByIdFromBlotter(state, tradeId) : undefined,
  );

  const errors = useAppSelector((state) => selectStopLossErrors(state, tileId));

  const limitPriceError = errors?.limitPrice;

  const validateStopLoss = useValidateStopLoss();

  const handleLimitPriceChange = async (value: number | undefined) => {
    track('Order action', 'Limit price changed');
    if (isDefined(orderFromBlotter)) {
      const response = await validateStopLoss({
        limitPrice: value,
      });

      if (response?.data?.validationResponseDetailContent.length === 0) {
        setLimitPrice(value);
      }
    }
  };

  return (
    <OrderRow labelId="order.fields.limitPrice.label">
      <OmsFieldErrorWrapper errors={limitPriceError}>
        <SgNumberInput
          valuePosition="start"
          selectAllOnFocus
          size="sm"
          maxDecimals={9}
          value={
            limitPrice ??
            (isDefined(orderFromBlotter?.limitPrice) ? (orderFromBlotter?.limitPrice as number) : undefined)
          }
          data-e2e="limit-price"
          onChange={handleLimitPriceChange}
          onFocus={() => setIsModifyingField(true)}
          onBlur={() => setIsModifyingField(false)}
          state={isDefined(limitPriceError) ? 'invalid' : undefined}
        />
      </OmsFieldErrorWrapper>
    </OrderRow>
  );
};
